/**
 * Accordion Functionality
 * Handles expanding/collapsing content, grouping, and deep linking.
 */

let activeAccordions = new Set();
let resizeListenerAdded = false;

/**
 * Initializes accordions within the given context.
 * @param {HTMLElement|Document} context
 */
export function initializeAccordions(context = document) {
    const accordions = context.querySelectorAll('.accordion');

    accordions.forEach(accordion => {
        // Prevent double initialization
        if (accordion.classList.contains('js-accordion-init')) return;
        accordion.classList.add('js-accordion-init');

        const title = accordion.querySelector('.accordion__title');
        const content = accordion.querySelector('.accordion__content');

        if (!title || !content) return;

        // Set initial state
        if (accordion.classList.contains('is-open')) {
            // If already open (e.g. server-side rendered or manually set), ensure height is set
            openAccordion(accordion);
        } else {
            // Ensure closed state is explicit for JS transitions
            content.style.height = '0px';
            title.setAttribute('aria-expanded', 'false');
            content.setAttribute('aria-hidden', 'true');
        }

        title.addEventListener('click', (e) => {
            e.preventDefault();
            toggleAccordion(accordion);
        });
    });

    // Handle Deep Linking via Hash
    if (window.location.hash) {
        const hash = window.location.hash.substring(1);
        if (hash) {
            const target = context.querySelector(`.accordion[data-target="${hash}"]`);
            if (target) {
                // Use setTimeout to allow layout to settle before calculating height/scrolling
                setTimeout(() => {
                    if (!target.classList.contains('is-open')) {
                        toggleAccordion(target);
                    }
                    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
            }
        }
    }

    // Global resize listener (added once)
    if (!resizeListenerAdded) {
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        resizeListenerAdded = true;
    }
}

/**
 * Cleans up active accordion references.
 * Should be called before page transitions.
 */
export function destroyAccordions() {
    activeAccordions.clear();
}

function toggleAccordion(accordion) {
    const isOpen = accordion.classList.contains('is-open');
    const groupId = accordion.dataset.groupId;

    if (isOpen) {
        closeAccordion(accordion);
    } else {
        // If part of a group, close others in the same group
        if (groupId) {
            const group = document.querySelectorAll(`.accordion[data-group-id="${groupId}"]`);
            group.forEach(acc => {
                if (acc !== accordion && acc.classList.contains('is-open')) {
                    closeAccordion(acc);
                }
            });
        }
        openAccordion(accordion);
    }
}

function openAccordion(accordion) {
    const content = accordion.querySelector('.accordion__content');
    const title = accordion.querySelector('.accordion__title');

    if (!content || !title) return;

    accordion.classList.add('is-open');
    title.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');

    // Set height to scrollHeight to animate open
    content.style.height = content.scrollHeight + 'px';

    activeAccordions.add(accordion);
}

function closeAccordion(accordion) {
    const content = accordion.querySelector('.accordion__content');
    const title = accordion.querySelector('.accordion__title');

    if (!content || !title) return;

    accordion.classList.remove('is-open');
    title.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');

    // Set height to 0 to animate closed
    content.style.height = '0px';

    activeAccordions.delete(accordion);
}

function onResize() {
    activeAccordions.forEach(accordion => {
        const content = accordion.querySelector('.accordion__content');
        if (content) {
            // Reset height to auto to recalculate scrollHeight correctly
            // This handles content reflow (wrapping text etc)
            content.style.height = 'auto';
            const newHeight = content.scrollHeight;
            // Re-apply pixel height
            requestAnimationFrame(() => {
                content.style.height = newHeight + 'px';
            });
        }
    });
}
